/**
 * Jumping in (1.06, owner decision, 29 October 2026, of the "Jump in" idea and its picture: "Add a jump in mechanic...
 * Space to jump, shows it on the screen, two airplanes, each carrying half of the playerbase, jump and parachute allows
 * for enough maneuvre to reach different places, near or far, you get the point, the ivsual you made is already good,
 * make that mechanic and test it please", the planes' ways drawn on the map as its two diagonals).
 *
 * A match's players come in by plane. Two planes fly over the map, one along each diagonal (bottom left to top right,
 * top left to bottom right; which end each starts from is rolled every match), and each is given half the players as
 * they join (`choosePlane` in utils/jumpIn.ts). The planes set off as the first player comes in. Space jumps (a phone
 * has a JUMP button); under the chute the movement keys steer, far enough to come down anywhere within `chuteReach()`
 * (about 660 units since 1.07, 580 before Fargo grew north) of the plane's way: from either plane the middle of each
 * side of the map, and its own way's two corners. Whoever is still aboard at the end of the way jumps there.
 *
 * Nobody in the air can be hurt or hurt anyone, nor pick anything up; nobody sees a player in a plane, and everyone in
 * sight sees one under a chute (over every roof). Nobody lands on a roof, in the water or on a solid: a chute aimed at
 * one glides in its last seconds (`settleMs`) to the open ground nearest where it's aimed, where the ring shows, and
 * comes down there (`nearestOpen`, `settleDirection` in utils/jumpIn.ts; 1.06.1: in 1.06 it came down on the roof and
 * was moved there as it landed, a jump of up to a hundred units off the museum). A player joining once the planes are
 * nearly across comes down by chute, over a spread spawn spot. The Training Range, a map with fixed spawns, and a
 * server with `jumpIn` off spawn players on the ground as before.
 *
 * Shared by the server (the planes, the jumps, the chutes) and the page (the planes drawn, the ring where you land, the
 * HUD). Distances are game units, times ms (the planes' clock: ms since the first player joined).
 */
export const JumpIn = Object.freeze({
    /** the planes come in from off the map this long before reaching the start of their way (the first to board wait) */
    boardMs: 3000,
    /** the second plane reaches its way this much after the first (the two don't cross the middle of the map together) */
    staggerMs: 2500,
    /** how far in from the map's corners the planes' ways start and end (over the sea) */
    inset: 24,
    /** the planes' speed (units a ms: across Fargo's diagonal in about 39 seconds since it grew north in 1.07, 35 before) */
    planeSpeed: 0.063,
    /** past the end of its way, a plane is drawn flying on this long */
    exitMs: 4000,
    /** a player just come aboard can't jump for this long (a key still held from the menu doesn't throw them out) */
    boardHoldMs: 800,
    /** with less than this left of a plane's way, a player joining doesn't board it */
    lateBoardMs: 8000,
    /** the last stretch of a plane's way, the HUD saying so (whoever's still aboard at its end jumps there) */
    warnMs: 5000,
    /** the height everyone jumps from (m): the HUD's height bar runs from it to the ground */
    height: 80,
    /** the way down, from the jump to the ground (free: braked all the way, `fallMs / brakeRate`) */
    fallMs: 12000,
    /**
     * the brake (1.09, the owner: "pressing space to glide slower down and reach further with the parachute"): with the
     * jump key held under the chute (a phone's BRAKE button), the way down goes this much as fast (0.6: 20 s from the
     * top held all the way, 12 s free), the glide across the ground as fast as ever, so further (`chuteReach()` is the
     * free fall's; braked all the way, 1 / brakeRate times it)
     */
    brakeRate: 0.6,
    /**
     * a chute's speed across the ground, steered all the way (units a ms: about twice a run). 1.07: 0.057, from 1.06's
     * 0.05, for Fargo grown 380 units north (the owner's picture: "new area uptop"): its land's farthest from both ways
     * went from 527 units to 600, the reach from 582 to 664, as far beyond it as before
     */
    glideSpeed: 0.057,
    /** how quickly a chute answers its steering (the time it takes to get two thirds of the way to a new heading) */
    steerMs: 350,
    /**
     * the last stretch of the way down (from about 27 m): a chute aimed at a roof, the water or a solid glides to the open
     * ground nearest where it's aimed (from the middle of the museum, the biggest building, under a hundred units; from a
     * plane's way's end over the sea, the shore some 200 units off), at up to `settleSpeed`
     */
    settleMs: 4000,
    /**
     * the fastest a chute glides to open ground then (units a ms: a gust, 1.4 times as fast as it's steered; 1.06.1's 0.07,
     * 1.07's 0.0798 with the glide)
     */
    settleSpeed: 0.0798,
    /**
     * the open ground it glides to has this much room round a player more (units): the nearest open ground is at the
     * edge of what's not, and a chute comes down within a unit or so of where it glides to
     */
    settleClearance: 2,
    /**
     * the open ground looked for round where a chute is aimed, or comes down: this far out at most, in rings this far
     * apart, twice as far apart past the first `landingNear` units (in the middle of the museum, under a thousand looks:
     * 1.06's audit, some 1,400 took a third of a tick)
     */
    landingSearch: 260,
    landingStep: 4,
    landingNear: 48,
    /** the view in the air: the camera shows this many units from the middle along the screen's long side */
    viewZoom: 145,
    /**
     * on a short screen (a phone's held on its side) the page lifts its camera as you come down, the ground point this
     * much of the screen under the middle (the HUD's top takes a third of it); the server's view reaches up as far
     * (1.06.1's audit: a band along the top of the screen wasn't sent)
     */
    shortLift: 0.2,
    /**
     * a screen under this many pixels tall (CSS) is a short one, its camera lifted under a chute: the page says so in
     * its input (`InputData.shortScreen`), and the server's view reaches up as the page's camera does, on a short screen
     * only (1.06.1's second review: it was by the screen's turn, landscape, and a desktop was sent a band above its top)
     */
    shortScreen: 600,
    /**
     * how far past each edge of a screen a chute's spot on the ground may be while the page still draws some of it on
     * the screen (units): from 80 m it draws the canopy 9 to 24 units up from the spot and up to 10.3 to its left, and
     * the chute's shadow round the spot, 10.1 to its right and 7.4 down at the most (client/src/scripts/managers/
     * jumpIn.ts: `airOffset`, `airScale`, the canopy's place and width, the shadow's). The server sends a chute that
     * far off the screen (1.06's audit: chutes popped in near the screen's bottom edge)
     */
    drawnBeyond: Object.freeze({ left: 11, top: 8, right: 11, bottom: 24 })
});
